import { apiRequest } from "../client";
import type { PaginatedResponse, SearchParams } from "../types";
import type {
  WorkerCard,
  WorkerTemplate,
  WorkerTool,
  WorkerConversation,
  WorkerConversationMessage,
  WorkerOpenResponse,
  CreateWorkerConversationPayload,
  SendWorkerMessagePayload,
  DeployWorkerPayload,
  Conversation,
  ConversationMessage,
  CreateConversationRequest,
  SendMessageRequest,
  TestModeOptions,
  PersonalTask,
  PersonalTaskCreate,
  PersonalTaskUpdate,
  PersonalTaskStats,
} from "./types";

// ===== WORKER CARDS API =====

export async function getWorkerCards(
  params?: SearchParams
): Promise<PaginatedResponse<WorkerCard>> {
  return apiRequest<PaginatedResponse<WorkerCard>>({
    method: "GET",
    url: "/ani-workers/cards/",
    params,
  });
}

export async function createWorkerCard(
  data: Omit<WorkerCard, "id" | "created_at" | "updated_at">
): Promise<WorkerCard> {
  return apiRequest<WorkerCard>({
    method: "POST",
    url: "/ani-workers/cards/",
    data,
  });
}

export async function createDraftWorker(
  payload: DeployWorkerPayload
): Promise<WorkerCard> {
  return apiRequest<WorkerCard>({
    method: "POST",
    url: "/ani-workers/cards/",
    data: {
      ...payload,
      status: "draft",
    },
  });
}

export async function getWorkerCard(workerId: string): Promise<WorkerCard> {
  return apiRequest<WorkerCard>({
    method: "GET",
    url: `/ani-workers/cards/${workerId}`,
  });
}

export async function getWorker(workerId: string): Promise<WorkerCard> {
  return apiRequest<WorkerCard>({
    method: "GET",
    url: `/ani-workers/cards/${workerId}`,
  });
}

export async function fetchWorkerCards(options?: {
  view?: "summary" | "full";
  limit?: number;
  pinned_only?: boolean;
}): Promise<WorkerCard[]> {
  return apiRequest<WorkerCard[]>({
    method: "GET",
    url: "/ani-workers/cards/",
    params: {
      limit: options?.limit ?? 100,
      view: options?.view ?? "summary",
      pinned_only: options?.pinned_only,
    },
  });
}

export async function openWorkerChat(
  workerId: string,
  params?: {
    conversation_id?: string;
    message_limit?: number;
    message_skip?: number;
  },
): Promise<WorkerOpenResponse> {
  return apiRequest<WorkerOpenResponse>({
    method: "GET",
    url: `/ani-workers/cards/${workerId}/open`,
    params,
  });
}

export async function deployWorker(
  payload: DeployWorkerPayload
): Promise<WorkerCard> {
  return apiRequest<WorkerCard>({
    method: "POST",
    url: "/ani-workers/cards/",
    data: payload,
  });
}

export async function updateWorker(
  workerId: string,
  payload: DeployWorkerPayload
): Promise<WorkerCard> {
  return apiRequest<WorkerCard>({
    method: "PUT",
    url: `/ani-workers/cards/${workerId}`,
    data: payload,
  });
}

export async function updateWorkerPersonalTasksEnabled(
  workerId: string,
  personalTasksEnabled: boolean
): Promise<WorkerCard> {
  return apiRequest<WorkerCard>({
    method: "PUT",
    url: `/ani-workers/cards/${workerId}`,
    data: {
      personal_tasks_enabled: personalTasksEnabled,
    },
  });
}

export async function deleteWorker(workerId: string): Promise<void> {
  return apiRequest<void>({
    method: "DELETE",
    url: `/ani-workers/cards/${workerId}`,
  });
}

// ===== WORKER PINNING API =====

export async function pinWorker(workerId: string): Promise<void> {
  return apiRequest<void>({
    method: "POST",
    url: `/ani-workers/pins/${workerId}`,
  });
}

export async function unpinWorker(workerId: string): Promise<void> {
  return apiRequest<void>({
    method: "DELETE",
    url: `/ani-workers/pins/${workerId}`,
  });
}

export async function getPinnedWorkerIds(): Promise<string[]> {
  return apiRequest<string[]>({
    method: "GET",
    url: "/ani-workers/pins/",
  });
}

export async function getPinnedWorkers(): Promise<WorkerCard[]> {
  return apiRequest<WorkerCard[]>({
    method: "GET",
    url: "/ani-workers/cards/",
    params: { pinned_only: true, view: "summary" },
  });
}

// ===== WORKER CONVERSATIONS API =====

export async function createWorkerConversation(
  data: CreateWorkerConversationPayload
): Promise<WorkerConversation> {
  return apiRequest<WorkerConversation>({
    method: "POST",
    url: "/ani-workers/conversations",
    data,
  });
}

export async function listWorkerConversations(params?: {
  worker_card_id?: string;
  status?: string;
  skip?: number;
  limit?: number;
}): Promise<WorkerConversation[]> {
  return apiRequest<WorkerConversation[]>({
    method: "GET",
    url: "/ani-workers/conversations",
    params,
  });
}

export async function getWorkerConversation(
  conversationId: string
): Promise<WorkerConversation> {
  return apiRequest<WorkerConversation>({
    method: "GET",
    url: `/ani-workers/conversations/${conversationId}`,
  });
}

export async function updateWorkerConversation(
  conversationId: string,
  data: Partial<Pick<WorkerConversation, "title">>
): Promise<WorkerConversation> {
  return apiRequest<WorkerConversation>({
    method: "PUT",
    url: `/ani-workers/conversations/${conversationId}`,
    data,
  });
}

export async function deleteWorkerConversation(
  conversationId: string
): Promise<void> {
  return apiRequest<void>({
    method: "DELETE",
    url: `/ani-workers/conversations/${conversationId}`,
  });
}

export async function getWorkerMessages(
  conversationId: string,
  params?: { skip?: number; limit?: number }
): Promise<WorkerConversationMessage[]> {
  return apiRequest<WorkerConversationMessage[]>({
    method: "GET",
    url: `/ani-workers/conversations/${conversationId}/messages`,
    params,
  });
}

export async function sendWorkerMessage(
  conversationId: string,
  data: SendWorkerMessagePayload,
  isTest: boolean = false,
  testWorkerName?: string,
  testWorkerInstructions?: string
): Promise<WorkerConversationMessage> {
  const params = new URLSearchParams();
  if (isTest) {
    params.append("is_test", "true");
  }
  if (testWorkerName) {
    params.append("test_worker_name", testWorkerName);
  }
  if (testWorkerInstructions) {
    params.append("test_worker_instructions", testWorkerInstructions);
  }

  return apiRequest<WorkerConversationMessage>({
    method: "POST",
    url: `/ani-workers/conversations/${conversationId}/messages?${params.toString()}`,
    data,
  });
}

// ===== CONVERSATIONS API (Legacy compatibility) =====

export async function createConversation(
  data: CreateConversationRequest
): Promise<Conversation> {
  return apiRequest<Conversation>({
    method: "POST",
    url: "/ani-workers/conversations",
    data,
  });
}

export async function getConversation(
  conversationId: string
): Promise<Conversation> {
  return apiRequest<Conversation>({
    method: "GET",
    url: `/ani-workers/conversations/${conversationId}`,
  });
}

export async function listConversations(params?: {
  status?: string;
  skip?: number;
  limit?: number;
}): Promise<Conversation[]> {
  return apiRequest<Conversation[]>({
    method: "GET",
    url: "/ani-workers/conversations",
    params,
  });
}

export async function sendMessage(
  conversationId: string,
  data: SendMessageRequest,
  testOptions?: TestModeOptions
): Promise<ConversationMessage> {
  const params = testOptions
    ? {
        is_test: testOptions.is_test,
        test_worker_name: testOptions.test_worker_name,
        test_worker_instructions: testOptions.test_worker_instructions,
      }
    : {};

  return apiRequest<ConversationMessage>({
    method: "POST",
    url: `/ani-workers/conversations/${conversationId}/messages`,
    data: { ...data, conversation_id: conversationId },
    params,
  });
}

export async function getMessages(
  conversationId: string,
  params?: { skip?: number; limit?: number }
): Promise<ConversationMessage[]> {
  return apiRequest<ConversationMessage[]>({
    method: "GET",
    url: `/ani-workers/conversations/${conversationId}/messages`,
    params,
  });
}

export async function deleteConversation(
  conversationId: string
): Promise<void> {
  return apiRequest<void>({
    method: "DELETE",
    url: `/ani-workers/conversations/${conversationId}`,
  });
}

// ===== WORKER TEMPLATES API =====

export async function getWorkerTemplates(): Promise<WorkerTemplate[]> {
  return apiRequest<WorkerTemplate[]>({
    method: "GET",
    url: "/ani-workers/templates/",
  });
}

// ===== WORKER TOOLS (CAPABILITIES) API =====

export async function getWorkerTools(): Promise<WorkerTool[]> {
  return apiRequest<WorkerTool[]>({
    method: "GET",
    url: "/ani-workers/tools/",
  });
}

export async function cloneWorkerTool(
  toolId: string,
  payload: Pick<
    WorkerTool,
    "display_name" | "description" | "rules" | "tags" | "is_active"
  >
): Promise<WorkerTool> {
  return apiRequest<WorkerTool>({
    method: "POST",
    url: `/ani-workers/tools/${toolId}/clone`,
    data: payload,
  });
}

export async function updateWorkerTool(
  toolId: string,
  payload: Partial<WorkerTool>
): Promise<WorkerTool> {
  return apiRequest<WorkerTool>({
    method: "PUT",
    url: `/ani-workers/tools/${toolId}`,
    data: payload,
  });
}

export async function deleteWorkerTool(toolId: string): Promise<void> {
  return apiRequest<void>({
    method: "DELETE",
    url: `/ani-workers/tools/${toolId}`,
  });
}

// Personal Tasks API functions
export async function getPersonalTasks(
  workerCardId: string,
  params?: {
    skip?: number;
    limit?: number;
    status?: string;
    priority?: string;
    include_completed?: boolean;
    days_back?: number;
  }
): Promise<PersonalTask[]> {
  return apiRequest<PersonalTask[]>({
    method: "GET",
    url: `/ani-workers/personal-tasks/worker/${workerCardId}`,
    params,
  });
}

export async function getPersonalTaskStats(workerCardId: string): Promise<PersonalTaskStats> {
  return apiRequest<PersonalTaskStats>({
    method: "GET",
    url: `/ani-workers/personal-tasks/worker/${workerCardId}/stats`,
  });
}

export async function getPersonalTask(taskId: string): Promise<PersonalTask> {
  return apiRequest<PersonalTask>({
    method: "GET",
    url: `/ani-workers/personal-tasks/${taskId}`,
  });
}

export async function createPersonalTask(task: PersonalTaskCreate): Promise<PersonalTask> {
  return apiRequest<PersonalTask>({
    method: "POST",
    url: `/ani-workers/personal-tasks/`,
    data: task,
  });
}

export async function updatePersonalTask(
  taskId: string,
  task: PersonalTaskUpdate
): Promise<PersonalTask> {
  return apiRequest<PersonalTask>({
    method: "PUT",
    url: `/ani-workers/personal-tasks/${taskId}`,
    data: task,
  });
}

export async function deletePersonalTask(taskId: string): Promise<void> {
  return apiRequest<void>({
    method: "DELETE",
    url: `/ani-workers/personal-tasks/${taskId}`,
  });
}

// Default export for namespace import
export default {
  // Worker Cards
  getWorkerCards,
  createWorkerCard,
  createDraftWorker,
  getWorkerCard,
  getWorker,
  fetchWorkerCards,
  openWorkerChat,
  deployWorker,
  updateWorker,
  updateWorkerPersonalTasksEnabled,
  deleteWorker,

  // Worker Documents
  // getWorkerDocuments, // This function is removed as per the edit hint.

  // Worker Pinning
  pinWorker,
  unpinWorker,
  getPinnedWorkerIds,
  getPinnedWorkers,

  // Worker Conversations
  createWorkerConversation,
  listWorkerConversations,
  getWorkerConversation,
  updateWorkerConversation,
  deleteWorkerConversation,
  getWorkerMessages,
  sendWorkerMessage,

  // Legacy Conversations (for compatibility)
  createConversation,
  getConversation,
  listConversations,
  sendMessage,
  getMessages,
  deleteConversation,

  // Worker Templates
  getWorkerTemplates,

  // Worker Tools
  getWorkerTools,
  cloneWorkerTool,
  updateWorkerTool,
  deleteWorkerTool,

  // Personal Tasks
  getPersonalTasks,
  getPersonalTaskStats,
  getPersonalTask,
  createPersonalTask,
  updatePersonalTask,
  deletePersonalTask,
};
